/**
 * ExecutiveSummary Component
 * Single Responsibility: Display and edit meeting summary
 * TASK-406: adds branded PPTX export download.
 */

import React, { useState } from 'react'
import { toast } from 'sonner'
import {
  getOrCreateBriefExport,
  getBriefPptxDownloadHref,
  ExportPendingBriefError,
} from '../../../services/api/exportApi'

interface MeetingExecutiveSummaryProps {
  summary: string
  timer: string
  sessionId: string
  onSummaryChange: (summary: string) => void
  onRegenerate: () => void
}

type ExportLanguage = 'en' | 'de' | 'fr'

const MeetingExecutiveSummary: React.FC<MeetingExecutiveSummaryProps> = ({
  summary,
  timer,
  sessionId,
  onSummaryChange,
  onRegenerate,
}) => {
  const [exporting, setExporting] = useState(false)
  const [language, setLanguage] = useState<ExportLanguage>('en')

  const handleExportPptx = async () => {
    if (!sessionId) {
      toast.error('Session not found')
      return
    }
    setExporting(true)
    try {
      const info = await getOrCreateBriefExport(sessionId, {
        language,
        regenerate: false,
        preview: false,
      })
      if (info.status === 'ready') {
        window.open(getBriefPptxDownloadHref(info.export_id), '_blank')
        if (info.degraded_sections && info.degraded_sections.length > 0) {
          toast.warning(
            `Deck generated — ${info.degraded_sections.length} section(s) had placeholder content. Review before sending.`,
          )
        } else {
          toast.success('PPTX deck ready')
        }
      } else if (info.status === 'failed') {
        toast.error(info.error_message || 'Export failed')
      } else {
        toast.message(`Export still ${info.status} — retry in a moment`)
      }
    } catch (err) {
      if (err instanceof ExportPendingBriefError) {
        toast.error('No brief content yet. Generate the brief first.')
      } else {
        const message = err instanceof Error ? err.message : 'Export failed'
        toast.error(message)
      }
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="bg-white rounded-xl p-6 border border-slate-200">
      <div className="flex justify-between items-center mb-4 pb-2 border-b border-slate-200">
        <h3 className="text-lg font-semibold text-kielis-navy flex items-center gap-2">
          📝 Executive Summary
          <span className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-500">
            {timer}
          </span>
        </h3>
        <div className="flex items-center gap-2">
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value as ExportLanguage)}
            disabled={exporting}
            className="px-2 py-1.5 text-sm border border-slate-300 rounded-lg bg-white focus:outline-none focus:border-blue-500"
            aria-label="Export language"
          >
            <option value="en">EN</option>
            <option value="de">DE</option>
            <option value="fr">FR</option>
          </select>
          <button
            onClick={handleExportPptx}
            disabled={exporting}
            className="px-3 py-1.5 bg-kielis-navy text-white rounded-lg text-sm hover:bg-slate-800 disabled:opacity-50 transition-all flex items-center gap-1.5"
          >
            {exporting ? '⏳ Preparing…' : '📄 Download PPTX'}
          </button>
          <button
            onClick={onRegenerate}
            className="px-3 py-1.5 bg-kielis-warning text-white rounded-lg text-sm hover:bg-yellow-600 transition-all"
          >
            🤖 Regenerate
          </button>
        </div>
      </div>
      <textarea
        value={summary}
        onChange={(e) => onSummaryChange(e.target.value)}
        className="w-full h-64 p-4 text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg resize-none focus:outline-none focus:border-blue-500 focus:bg-white"
        placeholder="Meeting summary will appear here..."
      />
      <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
        <div>
          💡 <strong>Tip:</strong> Edit summary to add personal context before
          sending
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span>Auto-saved</span>
        </div>
      </div>
    </div>
  )
}

export default MeetingExecutiveSummary
